import React, { useState, useMemo } from "react";
import Icon from "components/AppIcon";
import Image from "components/AppImage";


const COMPARABLES = [
{
  id: "c1",
  address: "5318 Farnam St",
  city: "Omaha, NE 68132",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1dfcce164-1772578642492.png",
  imageAlt: "Traditional two-story colonial home with red brick facade and white trim in Omaha Midtown residential neighborhood",
  status: "Sold",
  soldDate: "01/15/2026",
  price: 370000,
  pricePerSqft: 149,
  beds: 4,
  baths: 3,
  sqft: 2480,
  lotSize: 0.21,
  yearBuilt: 2007,
  dom: 8,
  condition: "Excellent",
  garage: 2,
  matchScore: 96,
  adjustment: -15000,
  adjustedPrice: 355000
},
{
  id: "c2",
  address: "4820 Dodge Street",
  city: "Omaha, NE 68132",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_18a88b4b0-1772578640097.png",
  imageAlt: "Charming craftsman style home with covered front porch, stone accents, and mature oak trees in Omaha Dundee neighborhood",
  status: "Sold",
  soldDate: "12/28/2025",
  price: 362000,
  pricePerSqft: 148,
  beds: 4,
  baths: 2.5,
  sqft: 2448,
  lotSize: 0.23,
  yearBuilt: 2010,
  dom: 14,
  condition: "Good",
  garage: 2,
  matchScore: 91,
  adjustment: +8500,
  adjustedPrice: 370500
},
{
  id: "c3",
  address: "6102 Cass Street",
  city: "Omaha, NE 68132",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_12516a52e-1772578640096.png",
  imageAlt: "Contemporary suburban home with gray siding, black shutters, and professionally landscaped front garden in Omaha Midtown",
  status: "Sold",
  soldDate: "01/02/2026",
  price: 378000,
  pricePerSqft: 152,
  beds: 4,
  baths: 3,
  sqft: 2490,
  lotSize: 0.20,
  yearBuilt: 2012,
  dom: 6,
  condition: "Excellent",
  garage: 2,
  matchScore: 88,
  adjustment: -22000,
  adjustedPrice: 356000
},
{
  id: "c4",
  address: "2215 S 52nd Avenue",
  city: "Omaha, NE 68106",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_176df3af8-1772578639980.png",
  imageAlt: "Modern ranch style home with attached garage, brick driveway, and well-maintained green lawn in Omaha Aksarben neighborhood",
  status: "Active",
  soldDate: "—",
  price: 397000,
  pricePerSqft: 160,
  beds: 4,
  baths: 3.5,
  sqft: 2474,
  lotSize: 0.25,
  yearBuilt: 2009,
  dom: 21,
  condition: "Good",
  garage: 2,
  matchScore: 83,
  adjustment: +12000,
  adjustedPrice: 409000
},
{
  id: "c5",
  address: "7834 Pacific Street",
  city: "Omaha, NE 68114",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f3388884-1772578640499.png",
  imageAlt: "Elegant two-story home with white columns, wraparound porch, and colorful flower beds along front walkway in Omaha West Omaha area",
  status: "Sold",
  soldDate: "01/20/2026",
  price: 365000,
  pricePerSqft: 148,
  beds: 3,
  baths: 3,
  sqft: 2469,
  lotSize: 0.19,
  yearBuilt: 2006,
  dom: 18,
  condition: "Good",
  garage: 1,
  matchScore: 79,
  adjustment: -5000,
  adjustedPrice: 360000
}];


const COLUMNS = [
{ key: "address", label: "Property", sortable: false, width: "min-w-[180px]" },
{ key: "status", label: "Status", sortable: true, width: "min-w-[90px]" },
{ key: "price", label: "Sale Price", sortable: true, width: "min-w-[120px]" },
{ key: "pricePerSqft", label: "$/Sq Ft", sortable: true, width: "min-w-[90px]" },
{ key: "sqft", label: "Sq Ft", sortable: true, width: "min-w-[80px]" },
{ key: "beds", label: "Beds", sortable: true, width: "min-w-[60px]" },
{ key: "baths", label: "Baths", sortable: true, width: "min-w-[60px]" },
{ key: "dom", label: "DOM", sortable: true, width: "min-w-[60px]" },
{ key: "lotSize", label: "Lot (ac)", sortable: true, width: "min-w-[80px]" },
{ key: "condition", label: "Condition", sortable: true, width: "min-w-[100px]" },
{ key: "adjustedPrice", label: "Adj. Price", sortable: true, width: "min-w-[120px]" },
{ key: "matchScore", label: "Match %", sortable: true, width: "min-w-[80px]" },
{ key: "actions", label: "", sortable: false, width: "min-w-[60px]" }];


const fmt = (n) => n?.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

function ConditionBadge({ condition }) {
  const map = { Excellent: "text-secondary bg-secondary/10", Good: "text-primary bg-primary/10", Fair: "text-warning bg-warning/10", Poor: "text-accent bg-accent/10" };
  return <span className={`text-xs font-caption font-semibold px-2 py-0.5 rounded-full ${map?.[condition] || "bg-muted text-muted-foreground"}`}>{condition}</span>;
}

function StatusBadge({ status }) {
  return (
    <span className={`text-xs font-caption font-semibold px-2 py-0.5 rounded-full ${status === "Sold" ? "bg-muted text-muted-foreground" : "bg-secondary/10 text-secondary"}`}>
      {status}
    </span>);

}

export default function ComparisonTable({ onRemove }) {
  const [sortKey, setSortKey] = useState("matchScore");
  const [sortDir, setSortDir] = useState("desc");
  const [removed, setRemoved] = useState([]);

  const handleSort = (key) => {
    if (!COLUMNS?.find((c) => c?.key === key)?.sortable) return;
    if (sortKey === key) setSortDir((d) => d === "asc" ? "desc" : "asc");else
    {setSortKey(key);setSortDir("desc");}
  };

  const handleRemove = (id) => {
    setRemoved((prev) => [...prev, id]);
    onRemove?.(id);
  };

  const sorted = useMemo(() => {
    const active = COMPARABLES?.filter((c) => !removed?.includes(c?.id));
    return [...active]?.sort((a, b) => {
      const av = a?.[sortKey],bv = b?.[sortKey];
      if (typeof av === "string") return sortDir === "asc" ? av?.localeCompare(bv) : bv?.localeCompare(av);
      return sortDir === "asc" ? av - bv : bv - av;
    });
  }, [sortKey, sortDir, removed]);

  const avgAdjusted = useMemo(() => {
    if (!sorted?.length) return 0;
    return sorted?.reduce((s, c) => s + c?.adjustedPrice, 0) / sorted?.length;
  }, [sorted]);

  return (
    <div className="card-base p-0 overflow-hidden">
      {/* Table header */}
      <div className="px-4 py-3 border-b border-border flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Icon name="Table2" size={15} color="var(--color-primary)" />
          <span className="font-heading font-semibold text-sm text-foreground">Comparable Properties</span>
          <span className="text-xs font-caption text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{sorted?.length} comps</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm font-caption text-muted-foreground">
            Avg Adj. Value: <span className="font-mono-data font-semibold text-primary">{fmt(avgAdjusted)}</span>
          </div>
        </div>
      </div>
      {/* Scrollable table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/60 border-b border-border">
              {COLUMNS?.map((col) =>
              <th
                key={col?.key}
                onClick={() => handleSort(col?.key)}
                className={`px-3 py-2.5 text-left text-xs font-caption font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap ${col?.width} ${col?.sortable ? "cursor-pointer hover:text-foreground select-none" : ""}`}>
                
                  <div className="flex items-center gap-1">
                    {col?.label}
                    {col?.sortable && sortKey === col?.key &&
                  <Icon name={sortDir === "asc" ? "ChevronUp" : "ChevronDown"} size={12} color="var(--color-primary)" />
                  }
                  </div>
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sorted?.map((comp) =>
            <tr key={comp?.id} className="hover:bg-muted/30 transition-standard group">
                {/* Address */}
                <td className="px-3 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
                      <Image src={comp?.image} alt={comp?.imageAlt} className="w-full h-full object-cover" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-caption font-medium text-foreground text-xs truncate max-w-[140px]">{comp?.address}</p>
                      <p className="text-xs text-muted-foreground truncate max-w-[140px]">{comp?.city}</p>
                      {comp?.soldDate !== "—" && <p className="text-xs text-muted-foreground">{comp?.soldDate}</p>}
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3"><StatusBadge status={comp?.status} /></td>
                <td className="px-3 py-3 font-mono-data text-xs font-semibold text-foreground whitespace-nowrap">{fmt(comp?.price)}</td>
                <td className="px-3 py-3 font-mono-data text-xs text-foreground">${comp?.pricePerSqft}</td>
                <td className="px-3 py-3 font-mono-data text-xs text-foreground">{comp?.sqft?.toLocaleString()}</td>
                <td className="px-3 py-3 font-mono-data text-xs text-foreground">{comp?.beds}</td>
                <td className="px-3 py-3 font-mono-data text-xs text-foreground">{comp?.baths}</td>
                <td className="px-3 py-3">
                  <span className={`font-mono-data text-xs font-semibold ${comp?.dom <= 10 ? "text-secondary" : comp?.dom <= 20 ? "text-warning" : "text-accent"}`}>
                    {comp?.dom}d
                  </span>
                </td>
                <td className="px-3 py-3 font-mono-data text-xs text-foreground">{comp?.lotSize}</td>
                <td className="px-3 py-3"><ConditionBadge condition={comp?.condition} /></td>
                <td className="px-3 py-3">
                  <div className="font-mono-data text-xs font-semibold text-foreground whitespace-nowrap">{fmt(comp?.adjustedPrice)}</div>
                  <div className={`text-xs font-caption ${comp?.adjustment >= 0 ? "text-secondary" : "text-accent"}`}>
                    {comp?.adjustment >= 0 ? "+" : ""}{fmt(comp?.adjustment)}
                  </div>
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-1">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-mono-data font-bold"
                  style={{ background: comp?.matchScore >= 90 ? "var(--color-secondary)" : comp?.matchScore >= 80 ? "var(--color-primary)" : "var(--color-warning)", color: "white" }}>
                      {comp?.matchScore}
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3">
                  <button
                  onClick={() => handleRemove(comp?.id)}
                  className="opacity-0 group-hover:opacity-100 w-7 h-7 flex items-center justify-center rounded-lg hover:bg-accent/10 transition-standard"
                  aria-label={`Remove ${comp?.address}`}>
                  
                    <Icon name="X" size={13} color="var(--color-accent)" />
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {sorted?.length === 0 &&
        <div className="py-12 text-center">
            <Icon name="Table2" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
            <p className="text-muted-foreground font-caption text-sm">All comparables removed. Reset filters to restore.</p>
          </div>
        }
      </div>
      {/* Summary row */}
      {sorted?.length > 0 &&
      <div className="px-4 py-3 border-t border-border bg-muted/30 flex flex-wrap gap-4 text-xs font-caption text-muted-foreground">
          <span>Avg Price: <strong className="text-foreground font-mono-data">{fmt(sorted?.reduce((s, c) => s + c?.price, 0) / sorted?.length)}</strong></span>
          <span>Avg $/Sq Ft: <strong className="text-foreground font-mono-data">${Math.round(sorted?.reduce((s, c) => s + c?.pricePerSqft, 0) / sorted?.length)}</strong></span>
          <span>Avg DOM: <strong className="text-foreground font-mono-data">{Math.round(sorted?.reduce((s, c) => s + c?.dom, 0) / sorted?.length)} days</strong></span>
          <span>Avg Adj. Value: <strong className="text-primary font-mono-data">{fmt(avgAdjusted)}</strong></span>
        </div>
      }
    </div>);

}